/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useRef, useState, useEffect } from 'react';
import { NodeProps, Position, Handle } from 'reactflow';
import { useDispatch } from 'react-redux';
import NodeHeader from 'src/components/ui/NodeHeader/NodeHeader';
import Input from 'src/components/ui/Input/Input';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import SelectVariableModal, { ListItem } from 'src/components/modals/SelectVariableModal/SelectVariableModal';
import { useTranslation } from 'react-i18next';
import { useCaseSectionsQuery } from 'src/services/case';
import { setNodeConfiguration } from 'src/redux/workflows/slice';
import { useOutsideClick } from 'src/hooks/utils';
import { useWorkflowsActionsPlaceholdersQuery } from 'src/services/workflows';

const CaseBuilderNode: React.FC<NodeProps> = ({ id, data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [topicValue, setTopicValue] = useState<string>('');
  const [sectionValue, setSectionValue] = useState<string>('');
  const [messageValue, setMessageValue] = useState<string>('');

  const [isMessageVariablesOpen, setIsMessageVariablesOpen] = useState(false);

  const { data: caseVariablesData, isLoading: isLoadingCaseVariables } = useWorkflowsActionsPlaceholdersQuery(
    'project.task',
    true,
  );

  const caseVariables: ListItem[] =
    caseVariablesData?.map(item => ({ variableName: `{${item.name}}`, variableLabel: `{${item.label}}` })) || [];

  useEffect(() => {
    if (data.actionData) {
      const { title, topic, message, returnValue, sectionId } = data.actionData;

      if (title && !topicValue) {
        setTopicValue(title);
      } else if (topic && !topicValue) {
        setTopicValue(topic);
      } else if (returnValue && !topicValue) {
        setTopicValue(returnValue);
      }

      if (message && !messageValue) {
        setMessageValue(message);
      } else if (returnValue && !messageValue) {
        setMessageValue(returnValue);
      }

      if (sectionId && !sectionValue) {
        setSectionValue(sectionId);
      }
    }
  }, [data.actionData, topicValue, messageValue, sectionValue]);

  const handleResetClick = () => {
    if (data.isEditMode && data.removeNode) {
      data.removeNode(id);
    } else if (data.isEditMode && data.removeLastNode) {
      data.removeLastNode();
      data.removeLastNode();
    }
  };

  const actionTitle = data.actionData?.title || 'Case Erstellen';
  const actionIcon = data.actionData?.icon || 'note';

  const updateNodeConfiguration = () => {
    dispatch(
      setNodeConfiguration({
        nodeId: id,
        nodeType: 'action',
        data: {
          actionType: 'case',
          title: topicValue || '',
          returnValue: messageValue || '',
          topic: topicValue,
          sectionId: sectionValue,
          message: messageValue,
        },
      }),
    );
  };

  useEffect(() => {
    if (topicValue) {
      updateNodeConfiguration();
    }
  }, [topicValue, sectionValue, messageValue]);

  const handleTopicChange = (value: string) => {
    if (!data.isEditMode) return;
    setTopicValue(value);
    updateNodeConfiguration();
  };

  const handleMessageChange = (value: string) => {
    if (!data.isEditMode) return;
    setMessageValue(value);
    updateNodeConfiguration();
  };

  const handleMessageVariableSelect = (variableName: string) => {
    const newValue = messageValue + variableName;
    setMessageValue(newValue);
    setIsMessageVariablesOpen(false);
    updateNodeConfiguration();
  };

  const modalRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsMessageVariablesOpen(false);
    }
  };
  useOutsideClick(modalRef, handleClickOutside);

  return (
    <div className="surface-elevated rounded-base border border-subtle relative !w-[48rem]">
      <div className="w-full">
        <div className="py-16 px-20">
          <NodeHeader
            title={t('workflows.create_case')}
            subtitle="Aktion"
            icon={actionIcon}
            onActionClick={handleResetClick}
            isEditMode={data.isEditMode}
          />
        </div>

        <div className="px-20">
          <div className="mb-16">
            <label className="block typo-text-12--bold text-full mb-8">{t('workflows.topic')}</label>
            <Input
              value={topicValue}
              onChange={e => handleTopicChange(e.target.value)}
              placeholder={t('workflows.case_topic_placeholder')}
              className="w-full"
              disabled={!data.isEditMode}
            />
          </div>
        </div>
        <div className="px-20">
          <div className="mb-16">
            <label className="block typo-text-12--bold text-full mb-8">{t('workflows.message')}</label>
            <div className="flex flex-col gap-4">
              {data.isEditMode && (
                <Button
                  icon="variable"
                  title={isLoadingCaseVariables ? t('general.loading_variables') : t('general.insert_variable')}
                  tone={ButtonToneProps.SECONDARY}
                  onClick={() => setIsMessageVariablesOpen(true)}
                  disabled={isLoadingCaseVariables}
                />
              )}
              <Textarea
                value={messageValue}
                onChange={e => handleMessageChange(e.target.value)}
                placeholder={t('workflows.message_placeholder')}
                disabled={!data.isEditMode}
                className="w-full"
              />
            </div>
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Right}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            right: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            left: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
        <Handle type="source" position={Position.Top} id={`${id}`} style={{ opacity: 0, top: '37px' }} />
        <Handle type="source" position={Position.Bottom} id={`${id}`} style={{ opacity: 0, top: '37px' }} />
        <Handle
          type="target"
          position={Position.Left}
          id={`${id}`}
          style={{
            opacity: 0,
            top: '37px',
            position: 'absolute',
            left: '0%',
            width: '1px !important',
            height: '1px !important',
          }}
        />
      </div>

      {isMessageVariablesOpen && (
        <div ref={modalRef} className="absolute left-[20rem] bottom-[2rem] z-30">
          <SelectVariableModal
            isOpen={isMessageVariablesOpen}
            onClose={() => setIsMessageVariablesOpen(false)}
            listItems={caseVariables || []}
            onItemSelect={handleMessageVariableSelect}
            isLoading={isLoadingCaseVariables}
          />
        </div>
      )}
    </div>
  );
};

export default CaseBuilderNode;
